import { useTranslation } from "react-i18next";import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/shared/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import { devicesAPI } from '@/shared/services/api';
import {
  Thermometer,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Bell,
  BellOff,
  RefreshCw,
  Settings,
  TrendingUp,
  TrendingDown,
  Minus,
  Snowflake,
  Flame,
  Clock,
  MapPin,
  BarChart3,
  Play,
  Pause } from
'lucide-react';
import { toast } from 'sonner';

// Temperature gauge visualization
const TemperatureGauge = ({ current, min, max, unit = '°C' }) => {const { t } = useTranslation("wms");
  const isInRange = current >= min && current <= max;
  const isCriticalLow = current < min;
  const isCriticalHigh = current > max;

  // Calculate position on gauge (0-100%)
  const range = max - min;
  const buffer = range * 0.3; // 30% buffer on each side
  const gaugeMin = min - buffer;
  const gaugeMax = max + buffer;
  const gaugeRange = gaugeMax - gaugeMin;
  const position = Math.min(100, Math.max(0, (current - gaugeMin) / gaugeRange * 100));

  return (
    <div className="relative h-4 bg-gradient-to-r from-blue-500 via-green-500 to-red-500 rounded-full overflow-hidden">
      {/* Safe zone indicator */}
      <div
        className="absolute h-full bg-green-200/50"
        style={{
          left: `${(min - gaugeMin) / gaugeRange * 100}%`,
          width: `${range / gaugeRange * 100}%`
        }} />
      
      {/* Current temperature marker */}
      <div
        className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white shadow-lg transition-all duration-500 ${
        isCriticalLow ? 'bg-blue-600' : isCriticalHigh ? 'bg-red-600' : 'bg-green-600'}`
        }
        style={{ left: `calc(${position}% - 8px)` }} />
      
    </div>);

};

// Sensor Card Component
const SensorCard = ({ sensor, onConfigure, onAcknowledge }) => {const { t } = useTranslation("wms");
  const current = sensor.current_temp ?? 0;
  const min = sensor.min_temp ?? 0;
  const max = sensor.max_temp ?? 10;
  const isInRange = current >= min && current <= max;
  const isCriticalLow = current < min;
  const isCriticalHigh = current > max;
  const hasAlert = !isInRange;

  // Determine trend (mock for demo)
  const trend = sensor.trend || 'stable';

  return (
    <Card className={`card-elevated transition-all ${
    hasAlert ? 'border-red-500 border-2 shadow-red-100' : ''}`
    }>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
            hasAlert ? 'bg-red-100 text-red-600' : 'bg-cyan-100 text-cyan-600'}`
            }>
              <Thermometer className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">{sensor.name}</h3>
              <p className="text-xs text-muted-foreground">{sensor.location}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Badge variant={sensor.status === 'online' ? 'default' : 'secondary'} className="text-xs">
              {sensor.status}
            </Badge>
            {hasAlert &&
            <Badge variant="destructive" className="text-xs gap-1">
                <AlertTriangle className="h-3 w-3" />{t("alert", "Alert")}

            </Badge>
            }
          </div>
        </div>

        {/* Temperature Display */}
        <div className="text-center py-4">
          <div className={`text-5xl font-bold ${
          isCriticalLow ? 'text-blue-600' : isCriticalHigh ? 'text-red-600' : 'text-green-600'}`
          }>
            {current.toFixed(1)}{t("c", "\xB0C")}
          </div>
          <div className="flex items-center justify-center gap-1 mt-1 text-sm text-muted-foreground">
            {trend === 'rising' && <TrendingUp className="h-4 w-4 text-red-500" />}
            {trend === 'falling' && <TrendingDown className="h-4 w-4 text-blue-500" />}
            {trend === 'stable' && <Minus className="h-4 w-4 text-gray-500" />}
            <span className="capitalize">{trend}</span>
          </div>
        </div>

        {/* Temperature Gauge */}
        <div className="mb-4">
          <TemperatureGauge current={current} min={min} max={max} />
          <div className="flex justify-between mt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Snowflake className="h-3 w-3 text-blue-500" />
              {min}{t("c", "\xB0C")}
            </span>
            <span className="text-green-600 font-medium">{t("safeRange", "Safe Range")}</span>
            <span className="flex items-center gap-1">
              <Flame className="h-3 w-3 text-red-500" />
              {max}{t("c", "\xB0C")}
            </span>
          </div>
        </div>

        {/* Status Info */}
        <div className="space-y-2 pt-3 border-t border-border">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />{t("lastReading", "Last Reading")}

            </span>
            <span>{new Date(sensor.last_seen).toLocaleTimeString()}</span>
          </div>
          {sensor.assigned_vehicle &&
          <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t("vehicle", "Vehicle")}</span>
              <span>{sensor.assigned_vehicle}</span>
            </div>
          }
        </div>

        {/* Alert Actions */}
        {hasAlert &&
        <div className="mt-4 pt-3 border-t border-red-200 flex gap-2">
            <Button
            size="sm"
            variant="destructive"
            className="flex-1 gap-1"
            onClick={() => onAcknowledge(sensor)}>
            
              <Bell className="h-3 w-3" />{t("acknowledge", "Acknowledge")}

          </Button>
            <Button
            size="sm"
            variant="outline"
            onClick={() => onConfigure(sensor)}>
            
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        }
      </CardContent>
    </Card>);

};

// Alert History Item
const AlertHistoryItem = ({ alert }) => {const { t } = useTranslation("wms");
  return (
    <div className={`p-3 rounded-lg border ${
    alert.type === 'high' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`
    }>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {alert.type === 'high' ?
          <Flame className="h-4 w-4 text-red-600" /> :

          <Snowflake className="h-4 w-4 text-blue-600" />
          }
          <div>
            <p className="font-medium text-sm">{alert.sensor_name}</p>
            <p className="text-xs text-muted-foreground">{alert.location}</p>
          </div>
        </div>
        <Badge variant={alert.acknowledged ? 'secondary' : 'destructive'} className="text-xs">
          {alert.acknowledged ? 'Acknowledged' : 'Active'}
        </Badge>
      </div>
      <div className="mt-2 flex items-center justify-between text-sm">
        <span className={alert.type === 'high' ? 'text-red-600' : 'text-blue-600'}>
          {alert.temperature}{t("c", "\xB0C (")}{alert.type === 'high' ? 'above' : 'below'}{t("threshold", "threshold)")}
        </span>
        <span className="text-xs text-muted-foreground">
          {new Date(alert.timestamp).toLocaleString()}
        </span>
      </div>
    </div>);

};

// Configure Sensor Dialog
const ConfigureSensorDialog = ({ open, onOpenChange, sensor, onSave }) => {const { t } = useTranslation("wms");
  const [minTemp, setMinTemp] = useState(sensor?.min_temp || 2);
  const [maxTemp, setMaxTemp] = useState(sensor?.max_temp || 8);
  const [alertEnabled, setAlertEnabled] = useState(sensor?.alert_threshold || true);

  useEffect(() => {
    if (sensor) {
      setMinTemp(sensor.min_temp || 2);
      setMaxTemp(sensor.max_temp || 8);
      setAlertEnabled(sensor.alert_threshold !== false);
    }
  }, [sensor]);

  const handleSave = () => {
    onSave({
      ...sensor,
      min_temp: parseFloat(minTemp),
      max_temp: parseFloat(maxTemp),
      alert_threshold: alertEnabled
    });
    onOpenChange(false);
  };

  if (!sensor) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />{t("configure", "Configure")}
            {sensor.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                <Snowflake className="h-4 w-4 text-blue-500" />{t("minTemperature", "Min Temperature")}

              </label>
              <Input
                type="number"
                value={minTemp}
                onChange={(e) => setMinTemp(e.target.value)}
                step="0.5" />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                <Flame className="h-4 w-4 text-red-500" />{t("maxTemperature", "Max Temperature")}

              </label>
              <Input
                type="number"
                value={maxTemp}
                onChange={(e) => setMaxTemp(e.target.value)}
                step="0.5" />
              
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              <span className="font-medium">{t("alertNotifications", "Alert Notifications")}</span>
            </div>
            <Button
              variant={alertEnabled ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAlertEnabled(!alertEnabled)}>
              
              {alertEnabled ? 'Enabled' : 'Disabled'}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel", "Cancel")}</Button>
          <Button onClick={handleSave}>{t("saveChanges", "Save Changes")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);

};

// Main Temperature Alerts Component
export const TemperatureAlerts = () => {const { t } = useTranslation("wms");
  const [loading, setLoading] = useState(true);
  const [sensors, setSensors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(true);
  const refreshInterval = useRef(null);

  // Mock alert history for demo
  const mockAlerts = [
  { id: 1, sensor_name: 'Cold Storage Sensor A', location: 'Cold Storage Zone A', type: 'high', temperature: 9.2, threshold: 8, timestamp: new Date(Date.now() - 3600000).toISOString(), acknowledged: false },
  { id: 2, sensor_name: 'Refrigerated Van Sensor', location: 'Vehicle - Van B', type: 'low', temperature: 1.5, threshold: 2, timestamp: new Date(Date.now() - 7200000).toISOString(), acknowledged: true }];


  // Fetch temperature sensors
  const fetchSensors = useCallback(async () => {
    try {
      const data = await devicesAPI.getDevices('temperature_sensor');

      // Add mock trend data for visualization
      const sensorsWithTrend = (data || []).map((s) => ({
        ...s,
        trend: ['rising', 'falling', 'stable'][Math.floor(Math.random() * 3)]
      }));

      setSensors(sensorsWithTrend);

      // Check for alerts
      const activeAlerts = sensorsWithTrend.filter((s) => {
        const current = s.current_temp ?? 0;
        return current < (s.min_temp || 0) || current > (s.max_temp || 10);
      });

      if (activeAlerts.length > 0 && isMonitoring) {
        activeAlerts.forEach((sensor) => {
          const isHigh = sensor.current_temp > sensor.max_temp;
          toast.error(`Temperature Alert: ${sensor.name}`, {
            description: `${sensor.current_temp}°C is ${isHigh ? 'above' : 'below'} the safe range`,
            duration: 10000
          });
        });
      }

      setAlerts(mockAlerts);
    } catch (error) {
      console.error('Failed to fetch temperature sensors:', error);
    } finally {
      setLoading(false);
    }
  }, [isMonitoring]);

  useEffect(() => {
    fetchSensors();

    if (isMonitoring) {
      refreshInterval.current = setInterval(fetchSensors, 30000); // Update every 30 seconds
    }

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [fetchSensors, isMonitoring]);

  const handleConfigure = (sensor) => {
    setSelectedSensor(sensor);
    setShowConfigDialog(true);
  };

  const handleSaveConfig = async (updatedSensor) => {
    try {
      await devicesAPI.updateDevice(updatedSensor.id, updatedSensor);
      toast.success('Sensor configuration updated');
      fetchSensors();
    } catch (error) {
      toast.error('Failed to update sensor configuration');
    }
  };

  const handleAcknowledge = (sensor) => {
    toast.success(`Alert acknowledged for ${sensor.name}`);
    // In production, this would update the alert status in the backend
  };

  const toggleMonitoring = () => {
    setIsMonitoring(!isMonitoring);
    if (!isMonitoring) {
      toast.success('Temperature monitoring enabled');
    } else {
      toast.info('Temperature monitoring paused');
    }
  };

  // Calculate stats
  const activeAlertCount = sensors.filter((s) => {
    const current = s.current_temp ?? 0;
    return current < (s.min_temp || 0) || current > (s.max_temp || 10);
  }).length;

  const onlineCount = sensors.filter((s) => s.status === 'online').length;
  const avgTemp = sensors.length > 0 ?
  (sensors.reduce((sum, s) => sum + (s.current_temp || 0), 0) / sensors.length).toFixed(1) :
  0;

  return (
    <div className="space-y-6" data-testid="temperature-alerts">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("temperatureMonitoring", "Temperature Monitoring")}</h1>
          <p className="text-muted-foreground mt-1">{t("coldChainAnd", "Cold chain and storage temperature alerts")}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={isMonitoring ? "default" : "outline"}
            onClick={toggleMonitoring}
            className="gap-2">
            
            {isMonitoring ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
            {isMonitoring ? 'Monitoring' : 'Paused'}
          </Button>
          <Button variant="outline" onClick={fetchSensors} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />{t("refresh", "Refresh")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("totalSensors", "Total Sensors")}</p>
              <p className="text-2xl font-bold">{sensors.length}</p>
            </div>
            <Thermometer className="h-8 w-8 text-muted-foreground/30" />
          </CardContent>
        </Card>
        <Card className={`card-elevated border-l-4 ${activeAlertCount > 0 ? 'border-l-red-500' : 'border-l-green-500'}`}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("activeAlerts", "Active Alerts")}</p>
              <p className={`text-2xl font-bold ${activeAlertCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {activeAlertCount}
              </p>
            </div>
            <AlertTriangle className={`h-8 w-8 ${activeAlertCount > 0 ? 'text-red-500/30' : 'text-green-500/30'}`} />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-cyan-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("avgTemperature", "Avg. Temperature")}</p>
              <p className="text-2xl font-bold text-cyan-600">{avgTemp}{t("c", "\xB0C")}</p>
            </div>
            <BarChart3 className="h-8 w-8 text-cyan-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("online", "Online")}</p>
              <p className="text-2xl font-bold text-blue-600">{onlineCount}/{sensors.length}</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-blue-500/30" />
          </CardContent>
        </Card>
      </div>

      {/* Active Alerts Banner */}
      {activeAlertCount > 0 &&
      <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("temperatureAlertsActive", "Temperature Alerts Active")}</AlertTitle>
          <AlertDescription>
            {activeAlertCount}{t("sensorsAreReporting", "sensor(s) are reporting temperatures outside the safe range. \n            Immediate action may be required.")}

        </AlertDescription>
        </Alert>
      }

      {/* Monitoring Status */}
      {isMonitoring &&
      <div className="flex items-center gap-2 text-sm">
          <span className="flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
          <span className="text-muted-foreground">{t("liveMonitoringActive", "Live monitoring active - refreshing every 30 seconds")}</span>
        </div>
      }

      {/* Sensor Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {loading ?
        Array.from({ length: 3 }).map((_, i) =>
        <Card key={i} className="card-elevated">
              <CardContent className="p-5 animate-pulse">
                <div className="h-6 bg-muted rounded w-3/4 mb-4" />
                <div className="h-16 bg-muted rounded mb-4" />
                <div className="h-4 bg-muted rounded w-full mb-2" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
        ) :
        sensors.length > 0 ?
        sensors.map((sensor) =>
        <SensorCard
          key={sensor.id}
          sensor={sensor}
          onConfigure={handleConfigure}
          onAcknowledge={handleAcknowledge} />

        ) :

        <Card className="card-elevated col-span-full">
            <CardContent className="p-12 text-center">
              <Thermometer className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noTemperatureSensors", "No Temperature Sensors")}</h3>
              <p className="text-muted-foreground">{t("addTemperatureSensors", "Add temperature sensors in Device Management to start monitoring.")}

            </p>
            </CardContent>
          </Card>
        }
      </div>

      {/* Alert History */}
      {alerts.length > 0 &&
      <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5" />{t("recentAlertHistory", "Recent Alert History")}

          </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {alerts.map((alert) =>
          <AlertHistoryItem key={alert.id} alert={alert} />
          )}
          </CardContent>
        </Card>
      }

      {/* Configure Dialog */}
      <ConfigureSensorDialog
        open={showConfigDialog}
        onOpenChange={setShowConfigDialog}
        sensor={selectedSensor}
        onSave={handleSaveConfig} />
      
    </div>);

};

export default TemperatureAlerts;